import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {LABYRINTH_GLOW_KIND} from '../../../configs/biomeEffects/labyrinthGlow';
import {TWO_PI, lerp, pickWeighted, randomSign, smoothstep, writeHiddenPose, writePose} from './biomeMath';

/**
 * ПРОБУЖДЕНИЕ ЛАБИРИНТОВ (подложка с каменными лабиринтами). Чистые функции
 * без Phaser и DOM: по этому описанию частицы каждый кадр ставит сцена
 * (scenes/FieldBiomeEffects).
 *
 * Пробуждение — один лабиринт: пятно света в его середине и кольца на его
 * бороздах. Кольца стоят на месте (борозды нарисованы), а бежит по ним свет:
 * кольцо за кольцом от середины к краю. Кольцо не загорается целиком — его
 * РИСУЕТ ПРОБЛЕСК: яркое пятно обходит кольцо по кругу и оставляет за собой
 * светящийся обод. Откуда проблеск начнет и в какую сторону пойдет (по часовой
 * стрелке или против) — жребий, свой у каждого кольца. Замкнувшись, обод
 * недолго держится и гаснет весь разом.
 *
 * Спрайт нельзя нарисовать частично, поэтому обод собран из коротких выгнутых
 * ОТРЕЗКОВ, уложенных внахлест по нарисованному овалу. Отрезок загорается,
 * когда через него проходит фронт света, — и обод растет отрезок за отрезком.
 * Первый отрезок лежит сразу за местом, откуда вышел проблеск, последний —
 * сразу перед ним: когда круг замыкается, они смыкаются так же, как любые два
 * соседа, — без щели и без двойной яркости.
 *
 * Какой лабиринт проснется, решает жребий по весам: спирали на поле — чаще,
 * лабиринты вне кадра — реже, только что светившийся — никогда.
 *
 * Дорожка лабиринтов помечена (LABYRINTH_GLOW_TAG): по метке сцена находит
 * светящийся лабиринт и, когда он в кадре игрока, играет «волшебный» ветер
 * (см. scenes/FieldEffectSounds). Рождаться свечению метка не мешает: сцена
 * его не придерживает.
 */

/** Метка дорожки лабиринтов: по ней сцена находит светящийся лабиринт */
export const LABYRINTH_GLOW_TAG = 'labyrinthGlow';

const ARC = 'arc';
const GLEAM = 'gleam';
const CORE = 'core';

/**
 * Отрезок обода (текстура — см. utils/effects/fieldBiomeTextures), в долях его
 * кадра: reachShare — на какой доле полукадра от середины отрезок сходит на
 * нет вдоль; bandShare — ширина светящейся полосы на половине яркости, высот
 * кадра; bowShare — прогиб дуги: на столько высот кадра концы отрезка ниже его
 * середины (нижней стороной кадра отрезок смотрит в центр кольца). Числа
 * повторяют формулу текстуры, тест сверяет их с самими пикселями
 */
export const LABYRINTH_RIM_SEGMENT = Object.freeze({
  texture: FIELD_BIOME_TEXTURES.RIM_SEGMENT, reachShare: 0.9, bandShare: 0.158, bowShare: 0.235,
});
// Мягкое пятно сходит на нет на этой доле полукадра
const DOT_REACH_SHARE = 0.9;

/**
 * Сколько отрезков в ободе кольца. Отрезок выгнут дугой, и прогиб у него свой
 * (bow — на сколько его концы ближе к центру кольца, чем середина). Дуга
 * кольца от середины отрезка до середины соседа прогибается на radius * (1 -
 * cos шага): отрезков берется столько, чтобы прогибы совпали, — тогда отрезки
 * ложатся на кольцо, а не срезают его хордами. Чем кольцо больше, тем оно
 * положе и тем больше в нем отрезков
 */
const resolvePieceCount = (radiusX, radiusY, bow, {min, max}) => {
  // Овал — по среднему радиусу: полуоси лабиринтов близки, точнее не нужно
  const stepRad = Math.acos(Math.max(-1, 1 - bow / ((radiusX + radiusY) / 2)));
  return Math.min(max, Math.max(min, Math.round(TWO_PI / stepRad)));
};

/** Поза частицы на ободе: в точке овала под углом angle, вдоль касательной к нему */
const writeRimPose = (out, group, particle, angle, alpha) => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // Касательная к овалу — всегда в одну сторону, куда бы ни шел проблеск:
  // тогда нижняя сторона кадра смотрит в центр кольца, и дуга отрезка выгнута
  // куда надо. Длина касательной — скорость точки овала по углу: на пологих
  // боках овала шаг между отрезками длиннее, чем на крутых, длиннее и отрезок
  const tangentX = -sin * particle.radiusX;
  const tangentY = cos * particle.radiusY;
  const width = particle.kind === ARC ? particle.lengthPerSpeed * Math.hypot(tangentX, tangentY) : particle.width;
  return writePose(
    out, group.x + cos * particle.radiusX, group.y + sin * particle.radiusY, alpha, width, particle.height,
    Math.atan2(tangentY, tangentX)
  );
};

const createTracks = (context, params) => {
  const {hexSize} = context;
  const {wave, rim, gleam} = params;
  const innerShare = params.rings[0].radiusShare;
  const outerShare = params.rings[params.rings.length - 1].radiusShare;
  // За сколько проблеск обходит кольцо: чем кольцо больше, тем дольше
  const sweepsMs = params.rings.map(({radiusShare}) => lerp(
    wave.sweepMs.min, wave.sweepMs.max,
    outerShare > innerShare ? (radiusShare - innerShare) / (outerShare - innerShare) : 1
  ));
  // Кольцо светится, пока рисуется, держится и гаснет; пробуждение кончается
  // с последним кольцом последнего прохода
  const durationMs = (wave.count - 1) * wave.periodMs + Math.max(...sweepsMs.map((sweepMs, ringIndex) => (
    ringIndex * wave.ringStaggerMs + sweepMs + wave.holdMs + wave.decayMs
  )));
  // Кадр отрезка выше светящейся полосы; прогиб отрезка — доля высоты кадра
  const pieceHeight = (hexSize * params.bandThicknessFactor) / LABYRINTH_RIM_SEGMENT.bandShare;
  const pieceBow = pieceHeight * LABYRINTH_RIM_SEGMENT.bowShare;

  // Лабиринты в мире и их частицы: картинка пейзажа на месте, и считать их
  // заново на каждое пробуждение незачем. Жребий пробуждения (откуда и куда
  // идет проблеск) лежит не в частицах, а в группе
  const labyrinths = params.labyrinths.map((labyrinth, index) => {
    const oval = context.toWorldOval(labyrinth);
    const particles = [];
    params.rings.forEach(({radiusShare, alphaShare}, ringIndex) => {
      const ring = {
        layer: FIELD_BIOME_LAYER_KEYS.GROUND_GLOW,
        tint: params.tint,
        ringIndex,
        radiusX: oval.radiusX * radiusShare,
        radiusY: oval.radiusY * radiusShare,
        delayMs: ringIndex * wave.ringStaggerMs,
        sweepMs: sweepsMs[ringIndex],
      };
      const alpha = params.peakAlpha * alphaShare;
      const pieceCount = resolvePieceCount(ring.radiusX, ring.radiusY, pieceBow, rim.pieceCount);
      const stepRad = TWO_PI / pieceCount;
      for (let pieceIndex = 0; pieceIndex < pieceCount; pieceIndex += 1) {
        particles.push({
          ...ring,
          kind: ARC,
          texture: LABYRINTH_RIM_SEGMENT.texture,
          pieceIndex,
          stepRad,
          // За столько фронт света проходит один шаг между отрезками
          stepMs: ring.sweepMs / pieceCount,
          // Отрезок тянется от середины одного соседа до середины другого:
          // вдвоем соседи дают ровную полосу. Кадр длиннее самого отрезка
          lengthPerSpeed: (2 * Math.sin(stepRad)) / LABYRINTH_RIM_SEGMENT.reachShare,
          height: pieceHeight,
          alpha,
        });
      }
      particles.push({
        ...ring,
        kind: GLEAM,
        texture: FIELD_BIOME_TEXTURES.DOT,
        width: hexSize * gleam.lengthFactor,
        height: hexSize * gleam.thicknessFactor,
        alpha: Math.min(1, alpha * gleam.alphaFactor),
      });
    });
    particles.push({
      kind: CORE,
      layer: FIELD_BIOME_LAYER_KEYS.GROUND_GLOW,
      texture: FIELD_BIOME_TEXTURES.DOT,
      tint: params.tint,
      width: (oval.radiusX * params.core.radiusShare * 2) / DOT_REACH_SHARE,
      height: (oval.radiusY * params.core.radiusShare * 2) / DOT_REACH_SHARE,
      alpha: params.peakAlpha * params.core.alphaShare,
      // Середина разгорается, пока рисуется первое кольцо, и гаснет вместе с ним
      delayMs: 0,
      sweepMs: sweepsMs[0],
    });
    return {index, oval, weight: labyrinth.weight, particles};
  });

  /** Видит ли камера лабиринт: его середина в кадре или недалеко за его краем */
  const checkIfInView = ({oval}, view) => {
    const reachX = oval.radiusX * params.viewReachShare;
    const reachY = oval.radiusY * params.viewReachShare;
    return oval.x >= view.left - reachX && oval.x <= view.right + reachX
      && oval.y >= view.top - reachY && oval.y <= view.bottom + reachY;
  };

  // Лабиринт, проснувшийся последним: дважды подряд один и тот же не светится.
  // Дорожки создаются заново на каждое поле и каждую смену ветра — память не
  // переживает ни того, ни другого, и это не страшно
  let lastIndex = null;

  const buildGroup = ({rng, view, liveGroups}) => {
    const litIndexes = (liveGroups || []).map((group) => group.labyrinthIndex);
    const dark = labyrinths.filter(({index}) => !litIndexes.includes(index));
    const rested = dark.filter(({index}) => index !== lastIndex);
    // Лабиринт на подложке один — отдыхать ему не с кем
    const candidates = rested.length > 0 ? rested : dark;
    const chosen = pickWeighted(rng, candidates, (candidate) => (
      candidate.weight * (view && !checkIfInView(candidate, view) ? params.outOfViewWeightShare : 1)
    ));
    if (!chosen) {
      return null;
    }
    lastIndex = chosen.index;
    // Жребий проблеска — свой у каждого кольца на каждом проходе: откуда он
    // начнет (угол на овале) и куда пойдет (1 — по часовой стрелке на экране,
    // -1 — против)
    const sweeps = params.rings.map(() => (
      Array.from({length: wave.count}, () => ({startAngle: rng() * TWO_PI, direction: randomSign(rng)}))
    ));
    return {
      durationMs, labyrinthIndex: chosen.index, x: chosen.oval.x, y: chosen.oval.y, sweeps, particles: chosen.particles,
    };
  };

  const resolvePose = (group, particle, elapsedMs, out) => {
    const ringMs = elapsedMs - particle.delayMs;
    if (!(ringMs > 0)) {
      return writeHiddenPose(out);
    }
    // Проход, который идет по кольцу сейчас. Проходы на кольце не
    // накладываются: следующий начинается, когда кольцо уже погасло
    const waveIndex = Math.min(wave.count - 1, Math.floor(ringMs / wave.periodMs));
    const sinceMs = ringMs - waveIndex * wave.periodMs;
    const litMs = particle.sweepMs + wave.holdMs;
    if (!(sinceMs < litMs + wave.decayMs)) {
      return writeHiddenPose(out);
    }
    // Замкнувшись, обод держится и гаснет весь разом
    const fade = 1 - smoothstep(litMs, litMs + wave.decayMs, sinceMs);
    if (particle.kind === CORE) {
      const kindle = smoothstep(0, particle.sweepMs, sinceMs);
      return writePose(out, group.x, group.y, particle.alpha * kindle * fade, particle.width, particle.height, 0);
    }

    const {startAngle, direction} = group.sweeps[particle.ringIndex][waveIndex];
    if (particle.kind === GLEAM) {
      // Проблеск едет на самом фронте света; обойдя круг, тает там, где начал
      const glow = smoothstep(0, gleam.fadeInMs, sinceMs)
        * (1 - smoothstep(particle.sweepMs, particle.sweepMs + gleam.fadeOutMs, sinceMs));
      if (!(glow > 0)) {
        return writeHiddenPose(out);
      }
      const turn = Math.min(1, sinceMs / particle.sweepMs);
      return writeRimPose(out, group, particle, startAngle + direction * TWO_PI * turn, particle.alpha * glow);
    }

    // Отрезок обода: фронт света доходит до него через pieceIndex шагов и за
    // один шаг проходит его от края до середины — за это время отрезок
    // загорается
    const frontMs = sinceMs - particle.pieceIndex * particle.stepMs;
    if (!(frontMs > 0)) {
      return writeHiddenPose(out);
    }
    const lit = smoothstep(0, particle.stepMs, frontMs);
    // Только что зажженный отрезок ярче обода и остывает до его яркости
    const afterglow = 1 + rim.flashShare * Math.exp(-Math.max(0, frontMs - particle.stepMs) / rim.afterglowMs);
    // Середина отрезка — в полушаге за началом своего шага: первый отрезок
    // лежит сразу за местом, откуда вышел проблеск, последний — сразу перед ним
    const angle = startAngle + direction * (particle.pieceIndex + 0.5) * particle.stepRad;
    return writeRimPose(out, group, particle, angle, Math.min(1, particle.alpha * lit * afterglow * fade));
  };

  return [{
    tag: LABYRINTH_GLOW_TAG,
    schedule: {firstDelayMs: params.firstDelayMs, intervalMs: params.intervalMs, maxGroups: params.maxGroups},
    buildGroup,
    resolvePose,
  }];
};

const labyrinthGlow = {
  kind: LABYRINTH_GLOW_KIND,
  // Ветер матча эффекту не нужен: при смене ветра его дорожки не трогаются
  usesWind: false,
  textures: [LABYRINTH_RIM_SEGMENT.texture, FIELD_BIOME_TEXTURES.DOT],
  createTracks,
};

export default labyrinthGlow;
